import { Box, HStack, Link, Spinner, Text, VStack } from "@chakra-ui/react";
import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../../../shared/hooks/useApi";
import type { InvoiceId, OfficeId, PatientId } from "../../../../../shared/schema/schema";
import { currencyFormatter } from "../../../../../shared/utils/currency-formatter";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import {
  EntityProfileEmptyState,
  EntityProfileErrorState,
} from "../../../../entityProfileModal/components/EntityProfileFeedback";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import type { PatientProfilePatient } from "../../../patientProfileModal.types";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patient?: PatientProfilePatient;
  patientId: PatientId;
};

type PatientInvoiceVisit = Record<string, unknown>;

type PatientInvoice = {
  contractTypeName?: string | null;
  currentInvoiceVisitDetails?: PatientInvoiceVisit[];
  endDate?: string | null;
  exportedStatus?: string | null;
  id: InvoiceId;
  manualHold?: boolean | null;
  officeId?: OfficeId | null;
  officeName?: string | null;
  paymentStatus?: string | null;
  pendingPrebillingIssueResolution?: boolean | null;
  startDate?: string | null;
  totalBilled?: number | null;
  totalBilledSeconds?: number | null;
  totalClaimNumbers?: string[] | null;
  totalPaid?: number | null;
  totalUnitsThousandths?: number | null;
};

type PatientInvoicesResponse = {
  invoices: PatientInvoice[];
};

type InvoiceSortKey =
  | "claimNumbers"
  | "contract"
  | "dateRange"
  | "exportStatus"
  | "id"
  | "manualHold"
  | "numberOfVisits"
  | "office"
  | "paymentStatus"
  | "pendingPrebillingIssueResolution"
  | "totalBilled"
  | "totalBilledHours"
  | "totalPayment"
  | "totalUnits";

const PAGE_SIZE = 10;

function formatBoolean(value: boolean | null | undefined) {
  if (value === null || value === undefined) {
    return "";
  }

  return value ? "Yes" : "No";
}

function formatCents(value: number | null | undefined) {
  return value === null || value === undefined ? "" : currencyFormatter.formatCents(value);
}

function formatDate(value: string | null | undefined) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  try {
    return dateFormatter.toDate(LocalDate.parse(value.slice(0, 10)));
  } catch {
    return value;
  }
}

function formatSeconds(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return "";
  }

  const totalMinutes = Math.round(value / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return `${hours}:${String(minutes).padStart(2, "0")} hr`;
}

function formatUnits(value: number | null | undefined) {
  return value === null || value === undefined ? "" : String(value / 1000);
}

function getInvoiceOfficeLabel(invoice: PatientInvoice, officeNameById: Map<OfficeId, string>) {
  if (invoice.officeName) {
    return invoice.officeName;
  }

  if (invoice.officeId === null || invoice.officeId === undefined) {
    return "";
  }

  return officeNameById.get(invoice.officeId) ?? `Office ${invoice.officeId}`;
}

function getInvoiceDateRange(invoice: PatientInvoice) {
  return `${formatDate(invoice.startDate)} - ${formatDate(invoice.endDate)}`;
}

function stopPropagation(callback: () => void) {
  return (event: React.MouseEvent) => {
    event.stopPropagation();
    callback();
  };
}

export default function PatientProfileBillingInvoicesSection(props: Props) {
  const { api, queries } = useApi();
  const officesQuery = useQuery(queries.office.list());
  const [pageNum, setPageNum] = React.useState(1);
  const invoicesQuery = useQuery({
    queryKey: ["patientProfileModal", props.patientId, "billingInvoices", pageNum],
    queryFn: () =>
      api.anyRequest<PatientInvoicesResponse>(
        "POST",
        "/agencies/:agencyId/agency_members/:agencyMemberId/patients/:patientId/invoices",
        {
          path: { patientId: props.patientId },
          body: { pageNum, pageSize: PAGE_SIZE },
        }
      ),
  });
  const invoices = invoicesQuery.data?.invoices ?? [];
  const officeNameById = React.useMemo(
    () => new Map((officesQuery.data?.offices ?? []).map((office) => [office.id, office.name])),
    [officesQuery.data?.offices]
  );
  const invoiceColumns = React.useMemo<
    Array<EntityProfileDataTableColumn<PatientInvoice, InvoiceSortKey>>
  >(
    () => [
      {
        header: "Invoice",
        key: "invoice",
        render: (invoice) => (
          <Link
            color="blue.600"
            fontWeight={700}
            pointerEvents={props.patient === undefined ? "none" : undefined}
            onClick={stopPropagation(() =>
              props.patient === undefined
                ? undefined
                : props.actions.openBillingInvoiceDetails({
                    patient: props.patient,
                    data: { invoiceId: invoice.id },
                  })
            )}
          >
            {invoice.id}
          </Link>
        ),
        sortKey: "id",
        sortValue: (invoice) => invoice.id,
      },
      {
        header: "Visits Date Range",
        key: "dateRange",
        render: getInvoiceDateRange,
        sortKey: "dateRange",
        sortValue: (invoice) => invoice.startDate ?? "",
      },
      {
        header: "Number Of Visits",
        key: "numberOfVisits",
        render: (invoice) => invoice.currentInvoiceVisitDetails?.length ?? 0,
        sortKey: "numberOfVisits",
        sortValue: (invoice) => invoice.currentInvoiceVisitDetails?.length ?? 0,
      },
      {
        header: "Office",
        key: "office",
        render: (invoice) => getInvoiceOfficeLabel(invoice, officeNameById),
        sortKey: "office",
        sortValue: (invoice) => getInvoiceOfficeLabel(invoice, officeNameById),
      },
      {
        header: "Contract",
        key: "contract",
        render: (invoice) => invoice.contractTypeName ?? "",
        sortKey: "contract",
        sortValue: (invoice) => invoice.contractTypeName ?? "",
      },
      {
        header: "Total Billed Hours",
        key: "totalBilledHours",
        render: (invoice) => formatSeconds(invoice.totalBilledSeconds),
        sortKey: "totalBilledHours",
        sortValue: (invoice) => invoice.totalBilledSeconds,
      },
      {
        cellAlign: "right",
        header: "Total Billed",
        headerAlign: "right",
        key: "totalBilled",
        render: (invoice) => formatCents(invoice.totalBilled),
        sortKey: "totalBilled",
        sortValue: (invoice) => invoice.totalBilled,
      },
      {
        cellAlign: "right",
        header: "Total Payment",
        headerAlign: "right",
        key: "totalPayment",
        render: (invoice) => formatCents(invoice.totalPaid),
        sortKey: "totalPayment",
        sortValue: (invoice) => invoice.totalPaid,
      },
      {
        cellAlign: "right",
        header: "Total Units",
        headerAlign: "right",
        key: "totalUnits",
        render: (invoice) => formatUnits(invoice.totalUnitsThousandths),
        sortKey: "totalUnits",
        sortValue: (invoice) => invoice.totalUnitsThousandths,
      },
      {
        header: "Claim Numbers",
        key: "claimNumbers",
        render: (invoice) => (invoice.totalClaimNumbers ?? []).join(", "),
        sortKey: "claimNumbers",
        sortValue: (invoice) => (invoice.totalClaimNumbers ?? []).join(", "),
      },
      {
        header: "Pending Prebilling Issue Resolution",
        key: "pendingPrebillingIssueResolution",
        render: (invoice) => formatBoolean(invoice.pendingPrebillingIssueResolution),
        sortKey: "pendingPrebillingIssueResolution",
        sortValue: (invoice) => formatBoolean(invoice.pendingPrebillingIssueResolution),
      },
      {
        header: "Manual Hold",
        key: "manualHold",
        render: (invoice) => formatBoolean(invoice.manualHold),
        sortKey: "manualHold",
        sortValue: (invoice) => formatBoolean(invoice.manualHold),
      },
      {
        header: "Export Status",
        key: "exportStatus",
        render: (invoice) => invoice.exportedStatus ?? "",
        sortKey: "exportStatus",
        sortValue: (invoice) => invoice.exportedStatus ?? "",
      },
      {
        header: "Payment Status",
        key: "paymentStatus",
        render: (invoice) => invoice.paymentStatus ?? "",
        sortKey: "paymentStatus",
        sortValue: (invoice) => invoice.paymentStatus ?? "",
      },
    ],
    [officeNameById, props.actions, props.patient]
  );

  return (
    <VStack alignItems="stretch" spacing={4}>
      {invoicesQuery.isLoading ? (
        <HStack color={entityProfileModalColors.muted} px={4} py={5}>
          <Spinner size="sm" />
          <Text>Loading invoices...</Text>
        </HStack>
      ) : invoicesQuery.isError ? (
        <EntityProfileErrorState label="invoices" onRetry={() => void invoicesQuery.refetch()} />
      ) : invoices.length === 0 ? (
        <EntityProfileEmptyState>Patient has no invoices</EntityProfileEmptyState>
      ) : (
        <Box>
          <EntityProfileDataTable
            columns={invoiceColumns}
            getRowKey={(invoice) => invoice.id}
            minW="1320px"
            rows={invoices}
            tableSx={{ tableLayout: "auto" }}
          />
        </Box>
      )}

      <HStack>
        <EntityModalButton
          disabled={pageNum === 1 || invoicesQuery.isFetching}
          kind="white"
          size="sm"
          onClick={() => setPageNum((current) => Math.max(1, current - 1))}
        >
          Previous
        </EntityModalButton>
        <EntityModalButton
          disabled={invoices.length < PAGE_SIZE || invoicesQuery.isFetching}
          kind="white"
          size="sm"
          onClick={() => setPageNum((current) => current + 1)}
        >
          Next
        </EntityModalButton>
      </HStack>
    </VStack>
  );
}
